'use client';

import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, Menu, X } from 'lucide-react';

import { routes } from '@workspace/routes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@workspace/ui/components/accordion';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from '@workspace/ui/components/dialog';
import { Logo } from '@workspace/ui/components/logo';
import { cn } from '@workspace/ui/lib/utils';

import { HERO_V3 } from '~/lib/landing-content';
import {
  isMarketingRouteActive,
  SHOW_PRODUCT_AND_RESOURCES
} from '~/lib/marketing-navigation';
import {
  isResourceNavigationActive,
  resourceNavigationGroups,
  resourceUtilityLinks
} from '~/lib/resource-navigation';
import {
  isProductNavigationActive,
  workflowHref,
  workflows
} from '~/lib/workflows';

export type MobileMenuProps = React.HTMLAttributes<HTMLDivElement>;

const rowClassName =
  'marketing-nav-link flex min-h-16 w-full items-center justify-between gap-4 rounded-none px-1 py-5 text-left text-base font-medium';
const childLinkClassName =
  'marketing-nav-item flex min-h-11 items-center gap-3 rounded-lg px-3 py-3 text-sm leading-snug transition-colors';

/** Full-screen, scrollable navigation; the dialog owns focus and scroll locking. */
export function MobileMenu({
  className,
  ...props
}: MobileMenuProps): React.JSX.Element {
  const pathname = usePathname();
  const [open, setOpen] = React.useState(false);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeMenu = () => setOpen(false);

  React.useEffect(() => {
    setOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    // A portalled modal must close when the desktop navigation takes over.
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setOpen(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  return (
    <div
      className={cn('flex items-center justify-between gap-4', className)}
      {...props}
    >
      <Link
        href={routes.marketing.Index}
        aria-label="Lumnis home"
        className="marketing-focus rounded-sm"
      >
        <Logo className="h-7 w-auto text-[20px]" />
      </Link>
      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="marketing-secondary-control size-11"
            aria-label="Open menu"
          >
            <Menu
              className="size-5"
              aria-hidden="true"
            />
          </Button>
        </DialogTrigger>
        <DialogContent
          className="marketing-portal mobile-navigation translate-x-0 translate-y-0"
          showCloseButton={false}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            closeButtonRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">Main navigation</DialogTitle>
          <div className="flex shrink-0 items-center justify-between border-b px-6 py-3 md:px-8">
            <Link
              href={routes.marketing.Index}
              aria-label="Lumnis home"
              className="marketing-focus rounded-sm"
              onClick={closeMenu}
            >
              <Logo className="h-7 w-auto text-[20px]" />
            </Link>
            <DialogClose asChild>
              <Button
                ref={closeButtonRef}
                variant="outline"
                size="icon"
                className="marketing-secondary-control size-11"
                aria-label="Close menu"
              >
                <X
                  className="size-5"
                  aria-hidden="true"
                />
              </Button>
            </DialogClose>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="flex min-h-full flex-col px-6 md:px-8">
              <nav
                aria-label="Main"
                className="pb-8 pt-6"
              >
                <Accordion
                  type="single"
                  collapsible
                >
                  {SHOW_PRODUCT_AND_RESOURCES && (
                  <AccordionItem value="product">
                    <AccordionTrigger
                      data-active={
                        isProductNavigationActive(pathname) || undefined
                      }
                      className={cn(
                        rowClassName,
                        'hover:no-underline [&>svg]:size-5'
                      )}
                    >
                      Product
                    </AccordionTrigger>
                    <AccordionContent className="space-y-1 pb-5">
                      {workflows.map((item, index) => (
                        <Link
                          key={item.slug}
                          href={workflowHref(item)}
                          aria-current={
                            pathname === workflowHref(item) ? 'page' : undefined
                          }
                          onClick={closeMenu}
                          className={childLinkClassName}
                        >
                          <span
                            className="shrink-0 font-mono text-[10px] opacity-60"
                            aria-hidden="true"
                          >
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          {item.title}
                        </Link>
                      ))}
                      <Link
                        href="/product#intelligence-explorer"
                        onClick={closeMenu}
                        className={cn(
                          childLinkClassName,
                          'justify-between text-muted-foreground'
                        )}
                      >
                        Explore all capabilities
                        <ArrowRight
                          className="size-4 shrink-0"
                          aria-hidden="true"
                        />
                      </Link>
                    </AccordionContent>
                  </AccordionItem>
                  )}
                  <Link
                    href={routes.marketing.Pricing}
                    onClick={closeMenu}
                    aria-current={
                      isMarketingRouteActive(pathname, '/pricing')
                        ? 'page'
                        : undefined
                    }
                    className={cn(rowClassName, 'border-b')}
                  >
                    Pricing
                    <ArrowRight
                      className="size-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </Link>
                  {SHOW_PRODUCT_AND_RESOURCES && (
                  <AccordionItem
                    value="resources"
                    className="border-b!"
                  >
                    <AccordionTrigger
                      data-active={
                        isResourceNavigationActive(pathname) || undefined
                      }
                      className={cn(
                        rowClassName,
                        'hover:no-underline [&>svg]:size-5'
                      )}
                    >
                      Resources
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <div className="mb-4 grid grid-cols-2 gap-1">
                        {resourceUtilityLinks.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={closeMenu}
                            aria-current={
                              isMarketingRouteActive(pathname, link.href)
                                ? 'page'
                                : undefined
                            }
                            className={childLinkClassName}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                      <Link
                        href="/resources"
                        onClick={closeMenu}
                        aria-current={
                          pathname === '/resources' ? 'page' : undefined
                        }
                        className={cn(
                          childLinkClassName,
                          'justify-between font-medium'
                        )}
                      >
                        Explore all resources
                        <ArrowRight
                          className="size-4 shrink-0"
                          aria-hidden="true"
                        />
                      </Link>
                      {resourceNavigationGroups.map((group) => (
                        <div
                          key={group.label}
                          className="mt-5"
                        >
                          <p className="px-3 pb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                            {group.label}
                          </p>
                          {group.links.map((link) => (
                            <Link
                              key={link.href}
                              href={link.href}
                              onClick={closeMenu}
                              aria-current={
                                pathname === link.href ? 'page' : undefined
                              }
                              className={childLinkClassName}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                  )}
                  <Link
                    href="/mcp"
                    onClick={closeMenu}
                    aria-current={
                      isMarketingRouteActive(pathname, '/mcp')
                        ? 'page'
                        : undefined
                    }
                    className={cn(rowClassName, 'border-b')}
                  >
                    MCP
                    <ArrowRight
                      className="size-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </Link>
                </Accordion>
              </nav>
              <div className="mt-auto flex flex-col gap-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
                <Button
                  asChild
                  className="marketing-cta marketing-cta--mobile w-full justify-between"
                >
                  <Link
                    href={routes.dashboard.auth.SignUp}
                    onClick={closeMenu}
                  >
                    {HERO_V3.cta}
                    <ArrowRight
                      className="size-4"
                      aria-hidden="true"
                    />
                  </Link>
                </Button>
                <Link
                  href={routes.dashboard.auth.SignIn}
                  onClick={closeMenu}
                  className="marketing-nav-link flex min-h-11 items-center justify-center rounded-lg text-sm font-medium"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
